import React from 'react';
import { Helmet } from 'react-helmet-async';
import { matchPath, useLocation } from 'react-router-dom';

// Tells search engines the one address each page lives at, so copies (www,
// other hostnames, ?query variants) count towards the real page. Games and
// replays are private rooms rather than pages to find, so they're kept out.

const SITE = 'https://play-sequence.com';

// Content pages, indexed at their own address.
const PAGES = ['/how-to-play', '/strategy', '/about', '/faq', '/privacy-policy', '/terms', '/contact', '/leaderboard', '/play-against-computer', '/play-with-friends'];

const canonicalFor = (pathname) => {
    const path = pathname.replace(/\/+$/, '') || '/';
    if (path === '/') return `${SITE}/`;
    // The classic look is the same game as the start page.
    if (path === '/classic') return `${SITE}/`;
    if (PAGES.includes(path)) return `${SITE}${path}`;
    return null;
};

const START_TITLE = 'Play Sequence Online Free – vs Computer or Friends';
const START_DESCRIPTION = 'Play Sequence free in your browser. Beat the computer on Easy, Medium or Hard, or invite friends with a link and video chat as you play. Try shuffled boards and climb the leaderboard. No sign-up.';

// What the site is, for search engines, on the start page only.
const STRUCTURED_DATA = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebSite',
            name: 'Play Sequence',
            url: `${SITE}/`,
        },
        {
            '@type': 'WebApplication',
            name: 'Play Sequence',
            url: `${SITE}/`,
            description: 'Play the Sequence board game online for free: against the computer (Easy, Medium or Hard) or with 2 to 12 friends by link, with built-in video calls and chat. No sign-up, no download.',
            applicationCategory: 'GameApplication',
            operatingSystem: 'Any (web browser)',
            isAccessibleForFree: true,
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            featureList: [
                'Play against the computer at Easy, Medium or Hard',
                'Invite 2 to 12 players by link, in 2 or 3 teams',
                'Built-in video calls and chat',
                'Online matchmaking',
                'Classic or shuffled board, optional turn timer',
                'Leaderboard, replays and rematches',
            ],
            publisher: { '@id': 'https://www.tedoratech.com/#organization' },
        },
        {
            '@type': 'Organization',
            '@id': 'https://www.tedoratech.com/#organization',
            name: 'Tedora Technologies',
            url: 'https://www.tedoratech.com',
            email: 'admin@tedoratech.com',
        },
    ],
});

const Canonical = () => {
    const { pathname } = useLocation();
    // My games sets its own noindex.
    if (matchPath('/my-games', pathname)) return null;
    const href = canonicalFor(pathname);
    const isStart = pathname.replace(/\/+$/, '') === '';
    return (
        <Helmet>
            {href
                ? <link rel="canonical" href={href} />
                : <meta name="robots" content="noindex" />}
            {/* The start page's own title and description (the same as in
                public/index.html), so coming back to it from a guide puts them back. */}
            {isStart && <title>{START_TITLE}</title>}
            {isStart && <meta name="description" content={START_DESCRIPTION} />}
            {isStart && <script type="application/ld+json">{STRUCTURED_DATA}</script>}
        </Helmet>
    );
};

export default Canonical;
